import { Box, Menu, MenuItem, Typography } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

export interface MapFeaturePickerOption {
  key: string | number;
  /** Headline for the row: the feature's name. */
  primary: string;
  /** One line under the name that tells overlapping features apart. */
  secondary?: string;
  /** Swatch matching the feature's fill on the map, for colour-coded layers. */
  color?: string;
}

interface MapFeaturePickerMenuProps {
  /** Viewport position of the click; null when nothing is being picked. */
  anchorPosition: { top: number; left: number } | null;
  options: MapFeaturePickerOption[];
  onSelect: (index: number) => void;
  onClose: () => void;
}

/**
 * Asks the user which feature they meant when a map click lands on several
 * stacked polygons of one layer (treatments, fire history). Opens only when
 * there is a real choice, two or more options; a single hit goes straight to
 * the layer's own popover.
 */
const MapFeaturePickerMenu = ({
  anchorPosition,
  options,
  onSelect,
  onClose,
}: MapFeaturePickerMenuProps) => (
  <Menu
    open={Boolean(anchorPosition) && options.length > 1}
    onClose={onClose}
    anchorReference="anchorPosition"
    anchorPosition={anchorPosition || undefined}
    PaperProps={{
      elevation: 6,
      sx: {
        mt: 1,
        minWidth: 260,
        px: 1,
        py: 0.5,
        borderRadius: "12px",
        bgcolor: "#1f2833",
        color: "#f8fafc",
        border: "1px solid rgba(148, 163, 184, 0.3)",
        boxShadow: "0 16px 32px rgba(15, 23, 42, 0.45)",
        "& .MuiMenuItem-root": {
          borderRadius: "10px",
          px: 2,
          py: 1.25,
          gap: 1.5,
          transition: "background-color 120ms ease, transform 120ms ease",
        },
        "& .MuiMenuItem-root:hover": {
          bgcolor: "rgba(148, 163, 184, 0.18)",
          transform: "translateX(2px)",
        },
      },
    }}
    MenuListProps={{
      sx: {
        py: 0.5,
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
      },
    }}
  >
    {options.map((option, index) => (
      <MenuItem key={option.key} onClick={() => onSelect(index)}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
            {option.color && (
              <Box
                component="span"
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  bgcolor: option.color,
                  border: "1px solid rgba(248, 250, 252, 0.4)",
                  flexShrink: 0,
                }}
              />
            )}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#f8fafc" }}>
                {option.primary}
              </Typography>
              {option.secondary && (
                <Typography variant="caption" sx={{ color: "rgba(148, 163, 184, 0.85)" }}>
                  {option.secondary}
                </Typography>
              )}
            </Box>
          </Box>
          <ChevronRightIcon sx={{ fontSize: 20, color: "rgba(148, 163, 184, 0.9)" }} />
        </Box>
      </MenuItem>
    ))}
  </Menu>
);

export default MapFeaturePickerMenu;
